import React, { FC, useState } from 'react';
import { FieldLabel, Text } from '@/components/atoms/AppText';
import {
  APP_BG,
  APP_BORDER,
  APP_HEX,
  APP_TEXT,
} from '@/constant/theme/colors.constant';
import FieldError from './FieldError';
import { tid } from '@/utils/testId';

interface ConfigInputProps {
  // ── Core ──────────────────────────────────────────────────────────────
  label: string | React.ReactNode;
  value?: string;
  /** Native change-event handler — receives the full React.ChangeEvent */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Native blur-event handler */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  type?: string;
  name?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  inputClassName?: string;

  // ── Styling ───────────────────────────────────────────────────────────
  focusRingColor?: string;
  iconColor?: string;
  icon?: React.ComponentType<{ className?: string }>;

  // ── Error handling ────────────────────────────────────────────────────
  /** String message OR boolean flag to show red border */
  error?: string | boolean;
  /** Explicit error message string (used alongside boolean `error`) */
  errorMsg?: string;

  // ── react-hook-form ───────────────────────────────────────────────────
  register?: any;
  formValidationEnabled?: boolean;
  validationSchema?: any;
  /** data-testid forwarded to the native <input> */
  dataTestId?: string;
}

const ConfigInput: FC<ConfigInputProps> = ({
  dataTestId,
  label,
  value,
  onChange,
  onBlur: onBlurProp,
  placeholder = '',
  type = 'text',
  name,
  readOnly = false,
  focusRingColor = 'rgb(var(--color-primary-rgb))',
  iconColor = 'text-primary',
  icon,
  error,
  errorMsg,
  required,
  disabled,
  inputClassName = '',
  register,
  formValidationEnabled = false,
  validationSchema,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const hasError = !!error;
  const displayMessage =
    errorMsg ?? (typeof error === 'string' ? error : undefined);
  // Only override border via inline style when focus/error needs a dynamic colour;
  // idle state is handled by Tailwind dark: variants on the className.
  const inlineBorder =
    hasError || isFocused
      ? { borderColor: hasError ? APP_HEX.danger : focusRingColor }
      : {};
  const Icon = icon;

  // Build react-hook-form registration props when register is provided
  const rhfProps =
    register && name && formValidationEnabled
      ? register(name, validationSchema ?? {})
      : register && name
        ? register(name)
        : {};

  return (
    <div>
      <label className="flex items-center gap-2 mb-2">
        {Icon && (
          <span
            className={`inline-flex items-center justify-center w-4 h-4 flex-shrink-0 ${iconColor}`}
          >
            <Icon />
          </span>
        )}
        <FieldLabel>{label}</FieldLabel>
        <Text
          as="span"
          size="xl"
          color="danger"
          className={`${required ? '' : 'invisible'}`}
        >
          *
        </Text>
      </label>
      <input
        {...rhfProps}
        data-testid={dataTestId ?? tid('config-input')}
        type={type}
        /* When RHF is active it manages the value internally; otherwise use controlled value */
        value={register ? undefined : (value ?? '')}
        name={rhfProps.name ?? name}
        onChange={(e) => {
          rhfProps.onChange?.(e);
          onChange?.(e);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          rhfProps.onBlur?.(e);
          onBlurProp?.(e);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
        style={inlineBorder}
        className={`appearance-none block w-full font-normal text-sm ${APP_TEXT.default} ${APP_BG.input} placeholder:text-gray-400 dark:placeholder:text-gray-600 placeholder:font-normal h-10 border ${APP_BORDER.default} rounded-lg py-2 px-3 outline-none transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed read-only:${APP_BG.base} read-only:cursor-default ${inputClassName}`}
      />
      <FieldError message={displayMessage} />
    </div>
  );
};

export default ConfigInput;
